import * as React from "react";
import { AlertTriangle, ExternalLink, Pencil, Plus, X, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client/react";
import { toast } from "sonner";

import { Card, CardContent } from "./ui/card";
import { ONTOLOGY_TYPE_ICON } from "../lib/ontologyTypeIcons";
import {
  ADD_ATTRIBUTE_VALUE,
  CONVERSATION_ATTRIBUTE_KINDS,
  REMOVE_ATTRIBUTE_VALUE,
  REPLACE_ATTRIBUTE_VALUE,
} from "../graphql/sharedDocuments";

/** The machine kinds: the first one a tenant has is what "add" offers first. */
const MACHINE_ATTR_KEYS = ["machine_instance", "machine_model"];

/** One `Conversation.attributes` row, as both hosts select it.
 *
 *  Typed structurally rather than off the generated GraphQL types: the two apps
 *  reach the attribute list through different documents (Neptune's
 *  ConversationFragment, Venus's AgentMessage query), and binding this component
 *  to either one would couple it to that document's shape. */
export interface ConversationAttributeItem {
  id: string;
  key: string;
  value: string;
  title: string;
  externalLink?: string | null;
  sortOrder?: number | null;
  /** The value it was expanded from ("Fall 44841"), or null. */
  expandedFrom?: string | null;
  /** Only ever non-null for the machine row. `null` means "not checkable" (no machine
   *  hierarchy for this tenant), which is NOT the same as "unknown machine". */
  recognized?: boolean | null;
}

/** The case the header links to when a conversation names several: the first one
 *  stored. A thread opens on its case; a later number in a body is more often noise. */
export function primaryServiceCase<T extends { key: string; createdAt?: string | null }>(
  attributes: readonly T[] | null | undefined,
): T | null {
  const cases = (attributes ?? []).filter((a) => a.key === "service_case");
  cases.sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""));
  return cases[0] ?? null;
}

interface ConversationAttributesCardProps {
  attributes: readonly ConversationAttributeItem[] | null | undefined;
  /** Message the edit is scoped to. Without it the card is read-only. */
  messageId?: string | null;
  /** Conversation still loading — renders placeholders instead of values. */
  loading?: boolean;
  /** Suppresses editing (e.g. while the host is in a read-only mode). */
  disabled?: boolean;
  /** Fired once an added or removed value is saved. The save schedules a
   *  regeneration server side while the host's old answer is still on screen, so
   *  without this nothing tells the service agent the pipeline is re-running. */
  onAttributesSaved?: () => void;
  className?: string;
  /** Without the tinted gradient, for a host whose background is already coloured. */
  flat?: boolean;
}

/**
 * The conversation's identifiers: cases, machines, customer numbers.
 *
 * The scan's reading is wrong often enough (a postal code read as a case, a
 * number quoted from an older ticket) that a service agent has to be able to fix
 * it value by value: edit one, remove a wrong one, add a missing one. A removed (or
 * edited) value never comes back, and what was expanded from it goes with it
 * (ADR-0065).
 */
export function ConversationAttributesCard({
  attributes,
  messageId,
  loading = false,
  disabled = false,
  className,
  onAttributesSaved,
  flat = false,
}: ConversationAttributesCardProps) {
  const { t } = useTranslation();
  const editable = !!messageId && !disabled;
  const onError = (error: Error) =>
    toast.error(
      t(
        "conversationAttributes.saveFailed",
        "Änderung konnte nicht gespeichert werden: {{message}}",
        { message: error.message },
      ),
    );
  const [addValue] = useMutation(ADD_ATTRIBUTE_VALUE, { onError });
  const [removeValue] = useMutation(REMOVE_ATTRIBUTE_VALUE, { onError });
  const [replaceValue] = useMutation(REPLACE_ATTRIBUTE_VALUE, { onError });
  const { data: kindsData } = useQuery(CONVERSATION_ATTRIBUTE_KINDS, { skip: !editable });
  const kinds = kindsData?.conversationAttributeKinds ?? [];

  const [isAdding, setIsAdding] = React.useState(false);
  const [draftKey, setDraftKey] = React.useState("");
  const [draftValue, setDraftValue] = React.useState("");
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  // The state above reaches the handlers only on the next render, so a second Enter
  // in the same tick would start a second save; this ref stops it at once.
  const inFlight = React.useRef(false);

  // Neither host keys or unmounts this card on ticket switch, so without this its
  // local state would leak from one conversation into the next.
  React.useEffect(() => {
    setIsAdding(false);
    setDraftValue("");
    setEditingId(null);
  }, [messageId]);

  const rows = [...(attributes ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  // One save at a time. The card holds no copy of the list: each mutation answers
  // with the conversation's attributes, and the cache hands them to the host.
  const save = async (mutate: (messageId: string) => Promise<boolean | undefined>) => {
    if (!messageId || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    try {
      if (await mutate(messageId)) onAttributesSaved?.();
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  const remove = (attr: ConversationAttributeItem) =>
    save(
      async (id) =>
        (await removeValue({ variables: { messageId: id, attributeId: attr.id } })).data
          ?.messageRemoveAttributeValue.changed,
    );

  const commitEdit = (attr: ConversationAttributeItem, input: string) => {
    const value = input.trim();
    setEditingId(null);
    if (!value || value === attr.value) return;
    void save(
      async (id) =>
        (await replaceValue({ variables: { messageId: id, attributeId: attr.id, value } })).data
          ?.messageReplaceAttributeValue.changed,
    );
  };

  const startAdding = () => {
    const machine = kinds.find((k) => MACHINE_ATTR_KEYS.includes(k.key));
    setDraftKey((current) => current || machine?.key || kinds[0]?.key || "");
    setDraftValue("");
    setIsAdding(true);
  };

  const commitAdd = () => {
    const value = draftValue.trim();
    const kind = kinds.find((k) => k.key === draftKey);
    setIsAdding(false);
    if (!value || !kind) return;
    if (rows.some((a) => a.key === kind.key && a.value === value)) return;
    void save(
      async (id) =>
        (await addValue({ variables: { messageId: id, key: kind.key, value } })).data
          ?.messageAddAttributeValue.changed,
    );
  };

  if (!loading && rows.length === 0 && !editable) return null;

  return (
    <Card
      className={`border-t-0 rounded-lg${className ? " " + className : ""}`}
      style={
        flat
          ? undefined
          : {
              background:
                "linear-gradient(to bottom right, rgba(52,90,239,0.06), rgba(213,113,229,0.10))",
            }
      }
    >
      <CardContent className="space-y-3 text-sm pt-3 px-1">
        {loading ? (
          <span className="block h-4 w-32 animate-pulse rounded bg-[#E6E8EF]" />
        ) : (
          <>
            {rows.map((attr) =>
              editable && editingId === attr.id ? (
                <EditRow
                  key={attr.id}
                  attr={attr}
                  onCommit={(value) => void commitEdit(attr, value)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <AttributeRow
                  key={attr.id}
                  attr={attr}
                  busy={saving}
                  onEdit={editable ? () => setEditingId(attr.id) : undefined}
                  onRemove={editable ? () => void remove(attr) : undefined}
                />
              ),
            )}
            {editable && isAdding && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <select
                    value={draftKey}
                    onChange={(e) => setDraftKey(e.target.value)}
                    aria-label={t("conversationAttributes.kind", "Art")}
                    className="shrink-0 rounded-sm border border-[#AEBDF9] bg-white px-1 py-0.5 text-sm"
                  >
                    {kinds.map((k) => (
                      <option key={k.key} value={k.key}>
                        {k.title}
                      </option>
                    ))}
                  </select>
                  {/* A borderless transparent input reads as static text and hides
                      the caret against the card's gradient — while open it has to
                      look like a field. */}
                  <input
                    autoFocus
                    value={draftValue}
                    onChange={(e) => setDraftValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitAdd();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        setIsAdding(false);
                      }
                    }}
                    aria-label={t("conversationAttributes.value", "Wert")}
                    className="min-w-0 flex-1 rounded-sm border border-[#AEBDF9] bg-white px-1.5 py-0.5 text-sm text-foreground caret-[#345AEF] outline-none focus-visible:border-[#345AEF] focus-visible:ring-1 focus-visible:ring-[#345AEF]"
                  />
                </div>
                <EditHints />
              </div>
            )}
            {editable && !isAdding && (
              <button
                type="button"
                onClick={startAdding}
                disabled={kinds.length === 0 || saving}
                className="inline-flex items-center gap-1 rounded-sm border border-dashed border-transparent px-1 text-muted-foreground hover:border-[#AEBDF9] hover:text-[#345AEF] disabled:cursor-default disabled:hover:border-transparent disabled:hover:text-muted-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
                {t("conversationAttributes.addValue", "Wert hinzufügen")}
              </button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function EditHints() {
  const { t } = useTranslation();
  return (
    <div className="pl-1 space-y-0.5">
      <p className="text-[11px] text-muted-foreground">
        {t("conversationAttributes.keyboardHint", "Enter zum Speichern, Esc zum Abbrechen")}
      </p>
      <p className="text-[11px] text-[#5D7BF2]">
        {t(
          "conversationAttributes.regenerateHint",
          "Speichern erzeugt die Antwort neu (inkl. Quellensuche) — das kann einen Moment dauern.",
        )}
      </p>
    </div>
  );
}

/** A kind's TBox icon, or as much space without one (a ticket, an order number), so
 * the rows' text stays aligned. A kind's key is its TBox type when one backs it. */
function KindIcon({ icon: Icon }: { icon: LucideIcon | undefined }) {
  return Icon ? (
    <Icon className="h-4 w-4 mr-2 text-muted-foreground shrink-0" />
  ) : (
    <span className="h-4 w-4 mr-2 shrink-0" aria-hidden />
  );
}

/** One value being edited in place: its kind stays, the value is pre-filled. */
function EditRow({
  attr,
  onCommit,
  onCancel,
}: {
  attr: ConversationAttributeItem;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const Icon = ONTOLOGY_TYPE_ICON[attr.key];
  const [value, setValue] = React.useState(attr.value);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <KindIcon icon={Icon} />
        <span className="shrink-0">{attr.title}:</span>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onCommit(value);
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancel();
            }
          }}
          aria-label={t("conversationAttributes.value", "Wert")}
          className="min-w-0 flex-1 rounded-sm border border-[#AEBDF9] bg-white px-1.5 py-0.5 text-sm text-foreground caret-[#345AEF] outline-none focus-visible:border-[#345AEF] focus-visible:ring-1 focus-visible:ring-[#345AEF]"
        />
      </div>
      <EditHints />
    </div>
  );
}

function AttributeRow({
  attr,
  busy = false,
  onEdit,
  onRemove,
}: {
  attr: ConversationAttributeItem;
  /** A save is in flight: the row's controls wait for its answer. */
  busy?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
}) {
  const { t } = useTranslation();
  const Icon = ONTOLOGY_TYPE_ICON[attr.key];
  const text = `${attr.title}: ${attr.value}`;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <KindIcon icon={Icon} />
        {attr.externalLink ? (
          <a
            href={attr.externalLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            {text}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span>{text}</span>
        )}
        {attr.expandedFrom && (
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {t("conversationAttributes.expandedFrom", "aus {{seed}}", {
              seed: attr.expandedFrom,
            })}
          </span>
        )}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            disabled={busy}
            aria-label={t("conversationAttributes.edit", "{{text}} bearbeiten", { text })}
            className="shrink-0 text-muted-foreground hover:text-[#345AEF]"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            aria-label={t("conversationAttributes.remove", "{{text}} entfernen", { text })}
            title={t(
              "conversationAttributes.removeHint",
              "Entfernen: wird für diese Konversation nicht wieder erkannt",
            )}
            className="shrink-0 text-muted-foreground hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {/* Strictly false: null is "no hierarchy to check against", and warning
          there would flag every correct number on such a tenant. */}
      {attr.recognized === false && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
          <p className="text-xs">
            {t(
              "conversationAttributes.unrecognized",
              "«{{value}}» ist nicht in unseren Maschinendaten. Prüfen Sie auf Tippfehler — die Antwort nutzt sonst allgemeine Quellen.",
              { value: attr.value },
            )}
          </p>
        </div>
      )}
    </div>
  );
}
